import type { DeviceViewSettingsTable } from '@ipfabric/utils';
import type {
  DeviceViewEntity,
  DeviceViewSettings,
  PathsWithName,
} from 'components/DeviceView/deviceViewTypes';
import { option } from 'fp-ts';
import { constNull, pipe } from 'fp-ts/lib/function';
import { distinct } from 'utils/array/distinct';
import { WIRELESS_ACCESS_POINTS, tablesInProtocols } from '../protocolTables';
import { isSourceGroupNode } from './isSourceGroupNode';

export function getDeviceViewEntityTables(
  deviceViewEntity: DeviceViewEntity,
  settings: DeviceViewSettings,
  pathsWithName: PathsWithName,
): DeviceViewSettingsTable[] {
  switch (deviceViewEntity.deviceViewElement) {
    case 'node': {
      if (deviceViewEntity.nodeType === null || !settings.nodesLoaded) {
        return [];
      }
      const tables =
        settings.nodes[deviceViewEntity.nodeType] ?? settings.nodes._any ?? [];
      return tables.filter(
        (table) => pathsWithName[table.webEndpoint] !== undefined,
      );
    }
    case 'edge': {
      if (!deviceViewEntity.edges || !deviceViewEntity.loaded) {
        return [];
      }
      const foundWebEndpoints = deviceViewEntity.edges.reduce(
        (acc: string[], edge): string[] => {
          if (edge.protocol === undefined) {
            return acc;
          }
          const protocolConfig = pipe(
            tablesInProtocols[edge.protocol],
            option.fromNullable,
            option.fold(constNull, (entityTables) =>
              deviceViewEntity.protocol === 'wireless' &&
              isSourceGroupNode(deviceViewEntity)
                ? entityTables.filter(
                    (table) => table !== WIRELESS_ACCESS_POINTS,
                  )
                : entityTables,
            ),
          );
          if (protocolConfig) {
            acc.push(...protocolConfig);
          }
          return acc;
        },
        [],
      );
      const webEndpoints = distinct(foundWebEndpoints).filter(
        (endpoint) => pathsWithName[endpoint] !== undefined,
      );
      return webEndpoints.map((endpoint) => ({
        // Fixed noUncheckedIndexedAccess NIM-19408
        label: pathsWithName[endpoint]!,
        webEndpoint: endpoint,
      }));
    }
    case 'labels':
    case 'packetDetails':
    case 'pathlookupDebug':
      return [];
    default:
      throw new Error('Unknown entity type');
  }
}
